import { useMemo, useState } from 'react';
import { MetaApp } from '../types/meta.types';
import { AppWindow, Plus, Webhook, Gauge, Power, Pencil, Trash2, Search, Lock, Globe, FlaskConical, Rocket, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface AppsListProps {
    apps: MetaApp[];
    onCreateApp: () => void;
    onEditApp: (app: MetaApp) => void;
    /** Abre modal compacto pra editar SÓ projects do app (sem form completo). */
    onEditAppProjects: (app: MetaApp) => void;
    onEditRateLimit: (app: MetaApp) => void;
    onDeleteApp: (app: MetaApp) => void;
    onToggleAppStatus: (app: MetaApp) => void;
    onViewWebhook: (app: MetaApp) => void;
    /** Resolve se o user atual pode gerenciar (editar/deletar/togglar) o app.
     *  Quando false, esconde os botões de write — só Webhook (read-only) e rate-limit-view ficam. */
    canManageApp?: (app: MetaApp) => boolean;
    /** Resolve se o user atual pode criar um app novo (esconde o botão "+ Novo App"). */
    canCreateApp?: boolean;
}

type VisibilityFilter = 'all' | 'public' | 'private';
type ModeFilter = 'all' | 'dev' | 'production';

export default function AppsList({
    apps,
    onCreateApp,
    onEditApp,
    onEditAppProjects,
    onEditRateLimit,
    onDeleteApp,
    onToggleAppStatus,
    onViewWebhook,
    canManageApp,
    canCreateApp = true,
}: AppsListProps) {
    const canManage = (app: MetaApp) => canManageApp ? canManageApp(app) : true;
    const [searchTerm, setSearchTerm] = useState('');
    const [visibility, setVisibility] = useState<VisibilityFilter>('all');
    const [mode, setMode] = useState<ModeFilter>('all');

    const filteredApps = useMemo(() => {
        return apps.filter(app => {
            // Considera multi-project (project_ids) com fallback legacy
            const projectsList = (app.projects && app.projects.length > 0)
                ? app.projects
                : (app.project_id ? [{ id: app.project_id, name: app.project_name ?? null }] : []);
            const projectNamesFlat = projectsList.map(p => (p.name || '').toLowerCase()).join(' ');

            const matchesSearch = !searchTerm ||
                app.app_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                app.app_id.includes(searchTerm) ||
                projectNamesFlat.includes(searchTerm.toLowerCase());

            const hasProjects = projectsList.length > 0;
            const matchesVisibility =
                visibility === 'all' ? true :
                visibility === 'public' ? !hasProjects :
                /* private */ hasProjects;

            const matchesMode =
                mode === 'all' ? true :
                app.meta_mode === mode;

            return matchesSearch && matchesVisibility && matchesMode;
        });
    }, [apps, searchTerm, visibility, mode]);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Apps</h1>
                    <p className="mt-2 text-gray-600">
                        Apps do Meta para autenticação e envio. Apps de desenvolvedor são privados a um usuário.
                    </p>
                </div>
                {canCreateApp && (
                    <Button
                        onClick={onCreateApp}
                        className="bg-[#0f172a] hover:bg-[#1e293b] text-white font-medium px-6 py-2.5 rounded-xl"
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        Novo App
                    </Button>
                )}
            </div>

            {/* Filtros */}
            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                <div className="flex flex-wrap items-end gap-4">
                    <div className="flex-1 min-w-48 space-y-1.5">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider ml-1">
                            Buscar
                        </label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                                placeholder="Nome, App ID, projeto..."
                                className="pl-9"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="w-44 space-y-1.5">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider ml-1">
                            Visibilidade
                        </label>
                        <Select value={visibility} onValueChange={(v) => setVisibility(v as VisibilityFilter)}>
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todas</SelectItem>
                                <SelectItem value="public">Públicos</SelectItem>
                                <SelectItem value="private">Privados</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="w-44 space-y-1.5">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider ml-1">
                            Modo no Meta
                        </label>
                        <Select value={mode} onValueChange={(v) => setMode(v as ModeFilter)}>
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos</SelectItem>
                                <SelectItem value="dev">Dev</SelectItem>
                                <SelectItem value="production">Production</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </div>

            <div className="px-1 text-sm text-gray-500">
                Mostrando <strong className="text-gray-900">{filteredApps.length}</strong> de <strong className="text-gray-900">{apps.length}</strong> apps
            </div>

            {/* Tabela */}
            <div className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aplicação</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visibilidade</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Modo Meta</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">App ID</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Páginas</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredApps.map((app) => (
                            <tr key={app.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-3">
                                        <div className="h-10 w-10 rounded-lg bg-gray-50 flex items-center justify-center border border-gray-100">
                                            <AppWindow className="h-5 w-5 text-gray-500" />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <p className="text-sm font-semibold text-gray-900">{app.app_name}</p>
                                                {app.is_default && (
                                                    <Badge variant="secondary" className="text-[10px] py-0 h-4 bg-gray-100">
                                                        Padrão
                                                    </Badge>
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-500 mt-0.5">{app.api_version || 'v18.0'}</p>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {(() => {
                                        // Multi-project com fallback legacy
                                        const projs = (app.projects && app.projects.length > 0)
                                            ? app.projects
                                            : (app.project_id ? [{ id: app.project_id, name: app.project_name ?? null }] : []);
                                        if (projs.length === 0) {
                                            return (
                                                <Badge variant="outline" className="border-emerald-200 bg-emerald-50/50 text-emerald-700">
                                                    <Globe className="h-3 w-3 mr-1" />
                                                    Público
                                                </Badge>
                                            );
                                        }
                                        return (
                                            <div className="flex flex-col gap-1 max-w-[260px]">
                                                <Badge variant="outline" className="border-violet-200 bg-violet-50/50 text-violet-700 w-fit">
                                                    <Lock className="h-3 w-3 mr-1" />
                                                    {projs.length === 1 ? 'Projeto' : `${projs.length} projetos`}
                                                </Badge>
                                                <div className="flex flex-wrap gap-1">
                                                    {projs.slice(0, 3).map((p) => (
                                                        <span
                                                            key={p.id}
                                                            className="text-xs text-gray-700 font-medium px-1.5 py-0.5 bg-gray-100 rounded truncate max-w-[200px]"
                                                            title={p.name || `Projeto #${p.id}`}
                                                        >
                                                            {p.name || `Projeto #${p.id}`}
                                                        </span>
                                                    ))}
                                                    {projs.length > 3 && (
                                                        <span
                                                            className="text-xs text-gray-500"
                                                            title={projs.slice(3).map(x => x.name || `Projeto #${x.id}`).join(', ')}
                                                        >
                                                            +{projs.length - 3}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {app.meta_mode === 'dev' ? (
                                        <Badge variant="outline" className="border-amber-200 bg-amber-50/50 text-amber-700">
                                            <FlaskConical className="h-3 w-3 mr-1" />
                                            Dev
                                        </Badge>
                                    ) : (
                                        <Badge variant="outline" className="border-blue-200 bg-blue-50/50 text-blue-700">
                                            <Rocket className="h-3 w-3 mr-1" />
                                            Production
                                        </Badge>
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-mono">
                                    {app.app_id}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                    {app.pages_connected ?? 0}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {app.is_active ? (
                                        <Badge variant="success">Ativo</Badge>
                                    ) : (
                                        <Badge variant="destructive">Pausado</Badge>
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right">
                                    <div className="flex justify-end gap-1">
                                        {/* Webhook viewer — read-only, sempre visível */}
                                        <Button variant="ghost" size="sm" onClick={() => onViewWebhook(app)} title="Webhook" className="h-9 w-9 p-0 text-blue-600 hover:bg-blue-50">
                                            <Webhook className="h-4 w-4" />
                                        </Button>
                                        {/* Demais ações modificam o app — só pra quem tem permissão */}
                                        {canManage(app) && (
                                            <>
                                                {/* Edição rápida APENAS dos projects vinculados — sem abrir form completo */}
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => onEditAppProjects(app)}
                                                    title="Projetos vinculados"
                                                    className="h-9 w-9 p-0 text-indigo-600 hover:bg-indigo-50"
                                                >
                                                    <Users className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => onEditRateLimit(app)}
                                                    title={`Limite: ${app.requests_per_second ?? 100}/s por página`}
                                                    className="h-9 w-9 p-0 text-violet-600 hover:bg-violet-50"
                                                >
                                                    <Gauge className="h-4 w-4" />
                                                </Button>
                                                <Button variant="ghost" size="sm" onClick={() => onToggleAppStatus(app)} title={app.is_active ? "Pausar" : "Ativar"} className="h-9 w-9 p-0">
                                                    <Power className={cn("h-4 w-4", app.is_active ? "text-orange-500" : "text-green-500")} />
                                                </Button>
                                                <Button variant="ghost" size="sm" onClick={() => onEditApp(app)} title="Editar" className="h-9 w-9 p-0 text-gray-600">
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                                {!app.is_default && (
                                                    <Button variant="ghost" size="sm" onClick={() => onDeleteApp(app)} title="Excluir" className="h-9 w-9 p-0 text-red-600">
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {filteredApps.length === 0 && (
                            <tr>
                                <td colSpan={7} className="px-6 py-12 text-center">
                                    <div className="flex flex-col items-center justify-center">
                                        <AppWindow className="h-10 w-10 text-gray-300 mb-3" />
                                        <p className="text-gray-500 text-sm">
                                            {apps.length === 0 ? 'Nenhum app cadastrado.' : 'Nenhum app corresponde aos filtros.'}
                                        </p>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
